import { z } from 'zod';

// NOTE: All fields are optional during form filling to allow users to:
// 1. Navigate between sections freely
// 2. Save progress at any time
// 3. Skip sections temporarily
// Required fields will be enforced only at final submission

// Common validators
export const emailSchema = z.string().email('Invalid email format');
export const phoneSchema = z.string().regex(/^\d{10,}$/, 'Phone number must be at least 10 digits');
export const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');

// Personal Info validators - all optional for progress saving
export const personalInfoSchema = z.object({
  fullName: z.string().optional(),
  date: z.string().optional(),
  phone: z.string().optional(),
  // email: emailSchema, // Email already collected at form start
  address: z.string().optional(),
  maritalStatus: z.enum(['married', 'divorced', 'single', '']).optional(),
  jobStatus: z.enum(['working', 'unemployed', 'student', '']).optional(),
  height: z.string().optional(),
  weight: z.string().optional(),
  gender: z.string().optional(),
  dob: z.string().optional(),
  ethnicity: z.string().optional(),
  birthPlace: z.string().optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactRelationship: z.string().optional(),
  emergencyContactPhone: z.string().optional(),
}).passthrough();

// Health Concerns validators - all optional for progress saving
export const healthConcernsSchema = z.object({
  mainConcern: z.string().optional(),
  problemFirstBegin: z.string().optional(),
  lifeAtTheTime: z.string().optional(),
  worseBetter: z.string().optional(),
  healthGoals: z.string().optional(),
  biggestObstacle: z.string().optional(),
  commitmentScale: z.number().min(1).max(10).optional(),
  takesMedsForConcern: z.enum(['yes', 'no', '']).optional(),
}).passthrough();

// Client Statement validators - all optional for progress saving
export const clientStatementSchema = z.object({
  clientNameStatement: z.string().optional(),
  signature: z.string().optional(),
  dateSigned: z.string().optional(),
}).passthrough();

// Partial validators for section-by-section validation
export const intakeFormSectionSchemas = {
  personalInfo: personalInfoSchema,
  healthConcerns: healthConcernsSchema,
  clientStatement: clientStatementSchema,
  // Add more sections as needed
};

// Complete form validator (for final submission)
export const completeIntakeFormSchema = z.object({
  personalInfo: personalInfoSchema,
  healthConcerns: healthConcernsSchema,
  clientStatement: clientStatementSchema,
  // Other sections can be more flexible
  practitionerQuestions: z.object({}).passthrough(),
  preExistingConditions: z.object({}).passthrough(),
  familyMedicalHistory: z.object({}).passthrough(),
  lifestyleProfile: z.object({}).passthrough(),
  sleepProfile: z.object({}).passthrough(),
  dentalProfile: z.object({}).passthrough(),
  toxinScreen: z.object({}).passthrough(),
  physicalActivity: z.object({}).passthrough(),
  dietaryProfile: z.object({}).passthrough(),
  supplementsMedications: z.object({}).passthrough(),
  psychosocialProfile: z.object({}).passthrough(),
  symptomAssessment: z.object({}).passthrough(),
  healthAndMedicalConditions: z.object({}).passthrough(),
});

// Validation helper functions
export function validateSection(section: string, data: any) {
  const schema = intakeFormSectionSchemas[section as keyof typeof intakeFormSectionSchemas];
  if (!schema) {
    return { success: true, data };
  }
  
  try {
    const validated = schema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { 
        success: false, 
        errors: error.issues.map(e => ({
          field: e.path.join('.'),
          message: e.message
        }))
      };
    }
    throw error;
  }
}

export function validateCompleteForm(data: any) {
  try {
    const validated = completeIntakeFormSchema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { 
        success: false, 
        errors: error.issues.map(e => ({
          field: e.path.join('.'),
          message: e.message
        }))
      };
    }
    throw error;
  }
}